/**
 * Pure compute layer สำหรับ classification คนไข้ใหม่/เก่า แบบ per-branch
 *
 * ดู spec: cliniter-new-patient-spec.pdf section 4 (Logic การตัดสิน)
 *
 * รับข้อมูล:
 *   - summary: visit aggregation จาก BE (GET /treatment-history/:patientId/visit-flags)
 *   - currentBranchId: สาขาที่ user login อยู่
 *   - patientBranches: per-branch override ของคนไข้ (จาก patient.patientBranches)
 *   - hadPriorHistory: มีประวัติก่อนใช้ระบบหรือไม่ (mechanism เดิม)
 *   - lapseDays: เกณฑ์ lapsed (default 365)
 */

export interface VisitBranchSummary {
  branchId: number
  branchCode: string
  visitCount: number
  firstVisitDate: string | null
  lastVisitDate: string | null
}

export interface VisitFlagsSummary {
  totalVisits: number
  firstVisitOrgPatientCaseId: number | null
  lastVisitDate: string | null
  branches: VisitBranchSummary[]
}

export type HeaderStatus =
  | 'BRAND_NEW'
  | 'NEW_AT_BRANCH'
  | 'EXISTING_AT_BRANCH'
  | 'LAPSED'

export type BranchVisitStatusOverride = 'NEW' | 'EXISTING' | null

export interface PatientBranchOverrideRow {
  branchId: number
  branchVisitStatusOverride?: BranchVisitStatusOverride
  branchVisitFirstDate?: string | Date | null
  branchVisitStatusOverrideAt?: string | Date | null
  createdAt?: string | Date
  branches?: { branchId: number; branchCode?: string; branchName?: string } | null
}

export interface OldestExistingBranchInfo {
  branchId: number
  branchCode: string
  sinceDate: string | null
}

export interface VisitFlagsInput {
  summary?: VisitFlagsSummary | null
  currentBranchId: number
  patientBranches?: PatientBranchOverrideRow[]
  hadPriorHistory?: boolean
  lapseDays?: number
}

export interface VisitFlagsResult {
  headerStatus: HeaderStatus
  isAmbiguous: boolean
  isLapsed: boolean
  hadPriorHistory: boolean
  visitCountOrg: number
  visitCountCurrentBranch: number
  lastVisitDate: string | null
  firstVisitOrgPatientCaseId: number | null
  oldestExistingBranch: OldestExistingBranchInfo | null
}

const DEFAULT_LAPSE_DAYS = 365

const toIso = (v: string | Date | null | undefined): string | null => {
  if (!v) return null
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

export function computeVisitFlags(input: VisitFlagsInput): VisitFlagsResult {
  const {
    summary,
    currentBranchId,
    patientBranches = [],
    hadPriorHistory = false,
    lapseDays = DEFAULT_LAPSE_DAYS
  } = input

  const summaryBranches = summary?.branches || []
  const visitCountOrg = summary?.totalVisits || 0
  const visitCountCurrentBranch =
    summaryBranches.find((b) => Number(b.branchId) === currentBranchId)?.visitCount || 0
  const firstVisitOrgPatientCaseId = summary?.firstVisitOrgPatientCaseId ?? null
  const lastVisitDate = summary?.lastVisitDate ?? null

  // override ที่สาขาปัจจุบัน
  const overrideAtCurrent: BranchVisitStatusOverride =
    patientBranches.find((pb) => Number(pb.branchId) === currentBranchId)?.branchVisitStatusOverride ?? null

  const hasOtherBranchExistingOverride = patientBranches.some(
    (pb) =>
      Number(pb.branchId) !== currentBranchId &&
      pb.branchVisitStatusOverride === 'EXISTING'
  )

  const isLapsed =
    lastVisitDate !== null &&
    Date.now() - new Date(lastVisitDate).getTime() > lapseDays * 24 * 60 * 60 * 1000

  const hasHistoryAtCurrent =
    visitCountCurrentBranch > 0 || overrideAtCurrent === 'EXISTING'
  const hasHistoryOrg =
    visitCountOrg > 0 ||
    overrideAtCurrent === 'EXISTING' ||
    hasOtherBranchExistingOverride ||
    hadPriorHistory

  const isAmbiguous = !hasHistoryAtCurrent && hasHistoryOrg

  let headerStatus: HeaderStatus

  if (hasHistoryAtCurrent) {
    headerStatus = 'EXISTING_AT_BRANCH'
  } else if (overrideAtCurrent === 'NEW') {
    headerStatus = hasHistoryOrg ? 'NEW_AT_BRANCH' : 'BRAND_NEW'
  } else if (isAmbiguous) {
    headerStatus = 'NEW_AT_BRANCH'
  } else {
    headerStatus = 'BRAND_NEW'
  }

  if (isLapsed && headerStatus === 'EXISTING_AT_BRANCH') {
    headerStatus = 'LAPSED'
  }

  // ===== คำนวณ oldestExistingBranch =====
  // รวม branches ที่มี real visit (จาก summary) และที่ override='EXISTING' (จาก patientBranches)
  const branchCodeMap = new Map<number, string>()
  for (const b of summaryBranches) {
    if (b.branchCode) branchCodeMap.set(Number(b.branchId), b.branchCode)
  }
  for (const pb of patientBranches) {
    const code = pb.branches?.branchCode
    if (code && !branchCodeMap.has(Number(pb.branchId))) {
      branchCodeMap.set(Number(pb.branchId), code)
    }
  }

  const branchesWithRealVisits = new Set<number>(summaryBranches.map((b) => Number(b.branchId)))

  const existingBranches: OldestExistingBranchInfo[] = []
  // มี real visit
  for (const b of summaryBranches) {
    existingBranches.push({
      branchId: Number(b.branchId),
      branchCode: branchCodeMap.get(Number(b.branchId)) || '',
      sinceDate: b.firstVisitDate
    })
  }
  // มี override='EXISTING' แต่ไม่มี real visit
  for (const pb of patientBranches) {
    const bid = Number(pb.branchId)
    if (pb.branchVisitStatusOverride === 'EXISTING' && !branchesWithRealVisits.has(bid)) {
      const sinceDate =
        toIso(pb.branchVisitFirstDate) ||
        toIso(pb.branchVisitStatusOverrideAt) ||
        toIso(pb.createdAt)
      existingBranches.push({
        branchId: bid,
        branchCode: branchCodeMap.get(bid) || '',
        sinceDate
      })
    }
  }

  existingBranches.sort((a, b) => {
    if (!a.sinceDate && !b.sinceDate) return 0
    if (!a.sinceDate) return 1
    if (!b.sinceDate) return -1
    return new Date(a.sinceDate).getTime() - new Date(b.sinceDate).getTime()
  })

  const oldestExistingBranch = existingBranches[0] || null

  return {
    headerStatus,
    isAmbiguous,
    isLapsed,
    hadPriorHistory,
    visitCountOrg,
    visitCountCurrentBranch,
    lastVisitDate,
    firstVisitOrgPatientCaseId,
    oldestExistingBranch
  }
}
